import { Fragment, useState, useEffect, useCallback } from "react";
import { Form, json, redirect, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  createCpaTest,
  getCpaTestAttempts,
} from "../../../api/OnboardCpa/fetchTest.js";
import OnboardCpaSteps from "../../ui/steps/OnboardCpaSteps.js";
import { Handler401 } from "../../../api/utils/responseActions.js";
import LoadingPlaceholder from "../../ui/loading/LoadingPlaceholder";
import { useRefreshOnAuth } from "../../../hooks/useRefreshOnAuth.js";

function CpaTestV2() {
  const { user } = useSelector((state) => state.auth);
  const role = user?.user_role;
  const cpa_status = user?.account_status;

  const [testAttemptsExceeded, setTestAttemptsExceeded] = useState(false);
  const [isCheckingAttempts, setIsCheckingAttempts] = useState(true);

  const checkTestAttempts = useCallback(async () => {
    try {
      setIsCheckingAttempts(true);
      const response = await getCpaTestAttempts();

      if (response.ok) {
        const data = await response.json();
        if (data.attempts >= data.maxAttempts) {
          setTestAttemptsExceeded(true);
        } else {
          // Reset state in case they were previously "exceeded" but status changed
          setTestAttemptsExceeded(false);
        }
      }
    } catch (error) {
      console.error("Error checking test attempts:", error);
    } finally {
      setIsCheckingAttempts(false);
    }
  }, []);

  // This will reload the data on session restore without unmount/mount the components
  useRefreshOnAuth(checkTestAttempts);

  // Check if user has exceeded test attempts on component mount
  useEffect(() => {
    // Only check if user has proper access
    if (role === "CPA" && cpa_status === "TN") {
      checkTestAttempts();
    } else {
      setIsCheckingAttempts(false);
    }
  }, [role, cpa_status]);

  // Check if user has access to this component
  if (role !== "CPA" || cpa_status !== "TN") {
    return (
      <Fragment>
        <div className="main-content-area">
          <div className="content">
            <OnboardCpaSteps />
            <div>
              <h1>Access Denied</h1>
              <p>You don't have permission to access this test.</p>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  // Redirect if user shouldn't be on this step
  // Test step is only for status "TN"
  if (cpa_status && cpa_status !== "TN") {
    return <Navigate to="/onboard" replace />;
  }

  return (
    <Fragment>
      <div className="main-content-area">
        <div className="content">
          <OnboardCpaSteps />

          {/* Show loading state while checking attempts */}
          {isCheckingAttempts && (
            <div className="internal-content-body">
              <LoadingPlaceholder rows={1} type="card-large" />
            </div>
          )}

          {/* Show test content normally */}
          {!isCheckingAttempts && !testAttemptsExceeded && (
            <Fragment>
              <div>
                <h1>Prove your expertise, once and done.</h1>
                <br />

                <p>
                  To keep our network top-tier, every pro completes a short
                  knowledge check.
                </p>
                <div id="terms">
                  <ul>
                    <li>Just 5 multiple-choice questions </li>
                    <li>30 minutes to answer</li>
                    <li>
                      Pass it once, and you're cleared for every future season
                      It's simple — and it shows clients you're the real deal."
                    </li>
                  </ul>
                </div>
              </div>

              <Form method="post">
                <button className="cta-button primary">
                  <b>Start the test</b>
                </button>
              </Form>
            </Fragment>
          )}

          {/* Show alternative content when attempts exceeded */}
          {!isCheckingAttempts && testAttemptsExceeded && (
            <div className="infoQuestions inProgress">
              <div>
                <h1>Test attempts exceeded.</h1>
                <p>
                  You have reached the maximum number of test attempts. Our
                  support team will review your case and get back to you within
                  24 hours. Please check your email for updates or contact
                  support directly.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default CpaTestV2;

export async function action({ request }) {
  // time when client initiate the test creation in milliseconds
  const startTest = new Date();
  const startTestMilliseconds = startTest.getTime();
  const data = { startTestMilliseconds: startTestMilliseconds };

  let endpoint = "utils/cpa-test";
  const response = await createCpaTest(request, endpoint, data);

  if (response.status === 401) {
    return Handler401();
  }

  if (response.status === 403) {
    // User has exceeded maximum test attempts - redirect back to test page
    return redirect("/onboard/onboarding-test");
  }

  const restData = await response.json();

  if (!response.ok) {
    throw json({ message: "Could not create user account." }, { status: 500 });
  }

  const url = "" + restData.testId;

  return redirect(url);
}
